import React, { useState, useEffect, useRef } from 'react';
import { servicesService } from './servicesService';
import { useAuth } from '../../hooks/useAuth';
import { Office, Currency } from '../../types';
import { Eye, EyeOff, X, Loader, Plus } from 'lucide-react';

interface AddServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingTags?: string[];
}

// Enhanced Tag Chip Input Component
interface TagChipInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  existingTags: string[];
  placeholder?: string;
}

const TagChipInput: React.FC<TagChipInputProps> = ({ tags, onTagsChange, existingTags, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputValue.trim().length > 0) {
      const filtered = existingTags
        .filter(tag =>
          tag.toLowerCase().includes(inputValue.toLowerCase()) &&
          !tags.includes(tag)
        )
        .slice(0, 8);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [inputValue, tags, existingTags]);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      onTagsChange([...tags, trimmedTag]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && filteredSuggestions.length > 0) {
        addTag(filteredSuggestions[0]);
      } else if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
    inputRef.current?.focus();
  };

  const isExistingTag = (tag: string) => {
    return existingTags.includes(tag);
  };

  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.trim().length > 0) setShowSuggestions(true);
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="w-full p-3 border rounded-lg"
          placeholder={placeholder || "Add tags..."}
        />

        {/* Add Button */}
        {inputValue.trim() && (
          <button
            type="button"
            onClick={() => addTag(inputValue)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-primary transition-colors"
          >
            <Plus size={20} />
          </button>
        )}

        {/* Suggestions Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-10 max-h-48 overflow-y-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between text-sm"
              >
                <span>{suggestion}</span>
                {index === 0 && (
                  <span className="text-xs text-gray-400">Press Enter</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tag Chips */}
      {tags.length > 0 && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  isExistingTag(tag)
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-green-100 text-green-800 border border-green-200'
                }`}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-red-600 transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Existing tags
            </span>
            <span className="ml-4 inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              New tags
            </span>
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-1">
        Type to see suggestions, press Enter to add new tags, or click suggestions to add existing ones.
      </p>
    </div>
  );
};

export const AddServiceModal: React.FC<AddServiceModalProps> = ({ isOpen, onClose, onSuccess, existingTags = [] }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    username: '',
    password: '',
    office: 'shared' as Office,
    category: '',
    billingCycle: 'monthly' as 'monthly' | 'yearly' | 'one-time' | 'free',
    cost: '',
    currency: 'USD' as Currency,
    renewalDay: '',
    renewalMonth: '',
    description: '',
    tags: [] as string[],
    notes: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.name.trim()) {
      setError('Service name is required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await servicesService.addService({
        name: formData.name.trim(),
        url: formData.url.trim(),
        username: formData.username.trim(),
        password: formData.password,
        office: formData.office,
        category: formData.category.trim(),
        billingCycle: formData.billingCycle,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        currency: formData.currency,
        renewalDate: formData.renewalDay
          ? new Date(2025, formData.renewalMonth ? parseInt(formData.renewalMonth) - 1 : 0, parseInt(formData.renewalDay))
          : null,
        description: formData.description.trim(),
        tags: formData.tags,
        notes: formData.notes.trim(),
        claimed: false,
        deleted: false,
        createdBy: user.email!,
      });

      // Reset form and close modal
      setFormData({
        name: '', url: '', username: '', password: '',
        office: 'shared', category: '', billingCycle: 'monthly',
        cost: '', currency: 'USD' as Currency, renewalDay: '', renewalMonth: '', description: '', tags: [], notes: '',
      });
      setShowPassword(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to add service');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setFormData({
      name: '', url: '', username: '', password: '',
      office: 'shared', category: '', billingCycle: 'monthly',
      cost: '', currency: 'USD' as Currency, renewalDay: '', renewalMonth: '', description: '', tags: [], notes: '',
    });
    setShowPassword(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Add Service</h2>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1: Name and URL */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Service Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="e.g., Adobe Creative Cloud"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">URL</label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="https://example.com"
                />
              </div>
            </div>

            {/* Row 2: Username and Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Username or email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full p-3 border rounded-lg pr-12"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Row 3: Office, Category, Billing Cycle */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Office</label>
                <select
                  value={formData.office}
                  onChange={(e) => setFormData(prev => ({ ...prev, office: e.target.value as any }))}
                  className="w-full p-3 border rounded-lg"
                >
                  <option value="stavanger">Stavanger</option>
                  <option value="tønsberg">Tønsberg</option>
                  <option value="oslo">Oslo</option>
                  <option value="shared">Shared</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="e.g., Design, Hosting"
                  list="category-suggestions"
                />
                <datalist id="category-suggestions">
                  <option value="3D" />
                  <option value="Accounting" />
                  <option value="AI" />
                  <option value="Analytics" />
                  <option value="Communication" />
                  <option value="Design" />
                  <option value="Development" />
                  <option value="Domains" />
                  <option value="Email" />
                  <option value="Fonts" />
                  <option value="Hosting" />
                  <option value="Marketing" />
                  <option value="Project Management" />
                  <option value="Security" />
                  <option value="Social Media" />
                  <option value="Stock" />
                  <option value="Storage" />
                  <option value="Video" />
                  <option value="WordPress Plugin" />
                  <option value="Other" />
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Billing Cycle</label>
                <select
                  value={formData.billingCycle}
                  onChange={(e) => setFormData(prev => ({ ...prev, billingCycle: e.target.value as any }))}
                  className="w-full p-3 border rounded-lg"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one-time">One-time</option>
                  <option value="free">Free</option>
                </select>
              </div>
            </div>

            {/* Row 4: Cost, Currency and Renewal Date */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Cost</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                    className="flex-1 p-3 border rounded-lg"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value as Currency }))}
                    className="w-20 p-3 border rounded-lg"
                  >
                    <option value="NOK">NOK</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="SEK">SEK</option>
                    <option value="DKK">DKK</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Renewal {formData.billingCycle === 'monthly' ? 'Day' : 'Date'}
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.renewalDay}
                    onChange={(e) => setFormData(prev => ({ ...prev, renewalDay: e.target.value }))}
                    className="flex-1 p-3 border rounded-lg"
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={String(d)}>{d}.</option>
                    ))}
                  </select>
                  {formData.billingCycle !== 'monthly' && (
                    <select
                      value={formData.renewalMonth}
                      onChange={(e) => setFormData(prev => ({ ...prev, renewalMonth: e.target.value }))}
                      className="flex-1 p-3 border rounded-lg"
                    >
                      <option value="">Month</option>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                        <option key={i} value={String(i + 1)}>{m}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* Row 5: Description */}
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-3 border rounded-lg h-20"
                placeholder="What is this service used for?"
              />
            </div>

            {/* Row 6: Tags */}
            <div>
              <label className="block text-sm font-medium mb-2">Tags</label>
              <TagChipInput
                tags={formData.tags}
                onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                existingTags={existingTags}
                placeholder="Add tags..."
              />
            </div>

            {/* Row 7: Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full p-3 border rounded-lg h-20"
                placeholder="Additional notes..."
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.name.trim() || submitting}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    Adding...
                  </>
                ) : (
                  'Add Service'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
